import { nextDate, nextEaster, nextLunarNewYear, nextUsaThanksgiving } from './holidays';

export interface InspirationIdea {
  id: string;
  category: InspirationCategory;
  photoUrl: string;
  backgroundAnimationUrl?: string;
  backgroundAnimationSearchQuery?: string;
  title: string;
  description?: string;
  weekday?: number;
  date?: Date;
  color?: string;
  keywords?: string[];
  prompt?: string;
  creator?: string;
}

export enum InspirationCategory {
  BD = 'Birthdays',
  CE = 'Celebrations',
  EA = 'Easter',
  EN = 'Entertaining',
  FJ = '4th of July',
  HA = 'Halloween',
  HO = 'Holiday',
  LN = 'Lunar New Year',
  NY = 'New Year\'s',
  SU = 'Summer',
  TG = 'Thanksgiving',
  VT = 'Valentine\'s',
  WI = 'Wicked'
}

export const getInspirationById = async (id: string): Promise<InspirationIdea | undefined> => {
  const inspirations = await getInspirations();

  return inspirations.find((inspiration) => inspiration.id === id);
};

const getInspirations = async (): Promise<InspirationIdea[]> => {
  return (await import(
    /* webpackChunkName: "inspiration" */
    /* webpackPrefetch: true */
    '../data/inspiration.data'
    )).default;
};

export const mapInspirations = (inspirations: InspirationIdea[]): Map<InspirationCategory, InspirationIdea[]> => {
  const inspirationMap: Map<InspirationCategory, InspirationIdea[]> = new Map();
  for (const inspiration of inspirations) {
    if (!inspirationMap.has(inspiration.category)) {
      inspirationMap.set(inspiration.category, []);
    }
    inspirationMap.get(inspiration.category)?.unshift(inspiration);
  }

  return inspirationMap;
};

export const getInspirationsByType = async (): Promise<Map<InspirationCategory, InspirationIdea[]>> => {
  return mapInspirations(
    await getInspirations()
  );
};

const getEnumKeyByEnumValue = <T extends { [index: string]: string }>(myEnum: T, enumValue: string): keyof T | null => {
  const keys = Object.keys(myEnum).filter(x => myEnum[x].toLowerCase() == enumValue.toLowerCase());
  return keys.length > 0 ? keys[0] : null;
};

export const getInspirationsByTags = async (tags: string[]): Promise<InspirationIdea[]> => {
  const InspirationsByType: Map<InspirationCategory, InspirationIdea[]> = mapInspirations(await getInspirations());

  let inspirations: InspirationIdea[] = [];

  for (const tag of tags) {
    const category = getEnumKeyByEnumValue(InspirationCategory, tag);
    if (category !== null) {
      inspirations = [...inspirations, ...(InspirationsByType.get(InspirationCategory[category]) ?? [])];
    }
  }

  return inspirations;
};

export const getFilteredInspirationsByType = async (tags: string[]): Promise<Map<InspirationCategory, InspirationIdea[]>> => {
  const inspirationMap: Map<InspirationCategory, InspirationIdea[]> = new Map();

  for (const inspiration of await getInspirationsByTags(tags)) {
    if (!inspirationMap.has(inspiration.category)) {
      inspirationMap.set(inspiration.category, []);
    }
    inspirationMap.get(inspiration.category)?.unshift(inspiration);
  }

  return inspirationMap;
};

export const getFilteredInspirationsBySearchTerm = async (searchTerm: string): Promise<Map<InspirationCategory, InspirationIdea[]>> => {
  const lcTerm = searchTerm.toLowerCase();
  return mapInspirations(
    (await getInspirations()).filter((inspiration) => {
      return inspiration.title.toLowerCase().includes(lcTerm) ||
        inspiration.description?.toLowerCase().includes(lcTerm) ||
        inspiration.category.toLowerCase().includes(lcTerm) ||
        (inspiration.keywords && inspiration.keywords.indexOf(lcTerm) !== -1);
    })
  );
};

export const sortedCategories = () => {
  const categoryDates: { [key in InspirationCategory]: number; } = {
    [InspirationCategory.WI]: 0,
    [InspirationCategory.BD]: 0,
    [InspirationCategory.CE]: 0,
    [InspirationCategory.EN]: 0,

    [InspirationCategory.LN]: nextLunarNewYear().getTime(),
    [InspirationCategory.VT]: nextDate(2, 14).getTime(),
    [InspirationCategory.EA]: nextEaster().getTime(),
    [InspirationCategory.FJ]: nextDate(7, 4).getTime(),
    [InspirationCategory.SU]: nextDate(9, 15).getTime(),
    [InspirationCategory.HA]: nextDate(10, 31).getTime(),
    [InspirationCategory.TG]: nextUsaThanksgiving().getTime(),
    [InspirationCategory.HO]: nextDate(12, 25).getTime(),
    [InspirationCategory.NY]: nextDate(12, 31).getTime(),
  };

  const sortedHolidays = [
    InspirationCategory.HO,
    InspirationCategory.NY,
    InspirationCategory.LN,
    InspirationCategory.VT,
    InspirationCategory.EA,
    InspirationCategory.FJ,
    InspirationCategory.SU,
    InspirationCategory.HA,
    InspirationCategory.TG,
  ].sort((a, b) => categoryDates[a] - categoryDates[b]);

  return [
    sortedHolidays.shift(),
    InspirationCategory.WI,
    InspirationCategory.BD,
    InspirationCategory.CE,
    ...sortedHolidays,
    InspirationCategory.EN
  ];
};

export const byInspirationType = (typeA: InspirationCategory, typeB: InspirationCategory): number => {
  const cats = sortedCategories();
  return cats.indexOf(typeA) - cats.indexOf(typeB);
};
